/**
 * Gizmo Keyboard Shortcuts Service
 *
 * Adds keyboard shortcuts to control the Inspector's gizmo mode via IGizmoService:
 * - W: Translate gizmo
 * - E: Rotate gizmo
 * - R: Scale gizmo
 * - T: Bounding Box gizmo
 * - Y: Planar drag gizmo (custom, outside Inspector's GizmoMode union)
 * - Q: Deactivate gizmo
 */

import type { ServiceDefinition, IGizmoService, GizmoMode } from '@babylonjs/inspector';
import { GizmoServiceIdentity } from '@babylonjs/inspector';
import { Observable } from '@babylonjs/core/Misc/observable';
import type { TraceScene } from 'trace-viewer';

/** Optional accessor so the service can check Babylon GUI focus state. */
let _traceSceneGetter: (() => TraceScene | null) | null = null;

/** Provide a TraceScene accessor for GUI focus checks. */
export function setGizmoKeyboardTraceScene(getter: () => TraceScene | null): void {
    _traceSceneGetter = getter;
}

const KEY_TO_MODE: Record<string, GizmoMode> = {
    'w': 'translate',
    'e': 'rotate',
    'r': 'scale',
    't': 'boundingBox',
};

/** Live IGizmoService handle, captured while the Inspector service is alive. */
let _gizmoService: IGizmoService | null = null;

/**
 * Temporarily hide all transform gizmos — the Inspector modes AND the custom
 * planar drag disc. Returns a restore function that re-applies the previous
 * state. Used by inline editors (e.g. viewport text editing) whose pointer
 * interactions would otherwise fight the gizmo handles for clicks.
 */
export function suppressTransformGizmos(): () => void {
    const service = _gizmoService;
    const prevMode = service?.gizmoMode;
    const prevPlanar = _planarModeActive;
    if (service) service.gizmoMode = undefined;
    setPlanarMode(false);
    return () => {
        // Only restore onto the same service instance — the Inspector
        // re-initializes on scene load, and a stale mode write to a fresh
        // service would stomp its default state.
        if (service && service === _gizmoService && prevMode !== undefined) {
            service.gizmoMode = prevMode;
        }
        setPlanarMode(prevPlanar);
    };
}

// ============================================================================
// Planar mode state (managed outside GizmoMode since it's a closed union)
// ============================================================================

/** Whether the custom planar drag mode is currently active. */
let _planarModeActive = false;

/** Observable that fires when planar mode changes. */
export const onPlanarModeChanged = new Observable<boolean>();

/** Returns true if the custom planar drag mode is active. */
export function isPlanarModeActive(): boolean {
    return _planarModeActive;
}

export function setPlanarMode(active: boolean): void {
    if (_planarModeActive === active) return;
    _planarModeActive = active;
    onPlanarModeChanged.notifyObservers(active);
}

/**
 * Service definition for the Gizmo Keyboard Shortcuts.
 * Consumes the GizmoService directly — no DOM hacking required.
 */
export const GizmoKeyboardServiceDefinition: ServiceDefinition<[], [IGizmoService]> = {
    friendlyName: 'Gizmo Keyboard Shortcuts',
    consumes: [GizmoServiceIdentity],

    factory: (gizmoService: IGizmoService) => {
        _gizmoService = gizmoService;

        // Play mode parks ALL manipulation gizmos (Unity game-view rule: the
        // running scene shows no editor chrome). The active mode is stashed
        // and restored on stop, and the mode hotkeys are inert while playing.
        // Read the live state rather than assuming stopped: the Inspector
        // re-initializes on scene load, which can happen mid-play.
        let inPlayMode = _traceSceneGetter?.()?.isPlayMode() ?? false;
        let stashedMode: GizmoMode | undefined;
        let stashedPlanar = false;

        // Mode changes funnel through here whatever the entrance — hotkeys,
        // the Inspector's own gizmo toolbar, suppressTransformGizmos. Gating
        // only the hotkeys would leave the toolbar buttons attaching a
        // translate/rotate/scale gizmo to running content.
        const modeObserver = gizmoService.onGizmoModeChanged.add(() => {
            if (gizmoService.gizmoMode === undefined) return;
            if (inPlayMode) {
                // Re-entrant by one hop: the write below fires this observer
                // again with `undefined`, which returns immediately above.
                gizmoService.gizmoMode = undefined;
                return;
            }
            // A built-in mode and planar drag are mutually exclusive.
            setPlanarMode(false);
        });
        const onPlayModeChanged = (e: Event) => {
            const playing = (e as CustomEvent<{ playMode: boolean }>).detail.playMode;
            if (playing === inPlayMode) return;
            inPlayMode = playing;
            if (playing) {
                stashedMode = gizmoService.gizmoMode;
                stashedPlanar = isPlanarModeActive();
                gizmoService.gizmoMode = undefined;
                setPlanarMode(false);
            } else {
                if (stashedMode !== undefined) gizmoService.gizmoMode = stashedMode;
                else setPlanarMode(stashedPlanar);
            }
        };
        window.addEventListener('trace-viewer:playmode-changed', onPlayModeChanged);

        const onKeyDown = (e: KeyboardEvent) => {
            // No gizmo hotkeys while the scene is running.
            if (inPlayMode) return;
            // Don't handle if user is typing in an input field
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                _traceSceneGetter?.()?.hasGUIFocus) {
                return;
            }

            // Don't handle if modifier keys are pressed (allow Ctrl+W to close tab, etc.)
            if (e.ctrlKey || e.metaKey || e.altKey) {
                return;
            }

            const key = e.key.toLowerCase();
            const mode = KEY_TO_MODE[key];
            if (mode) {
                e.preventDefault();
                gizmoService.gizmoMode = mode;
                // Built-in mode → deactivate planar (handled by modeObserver above)
            } else if (key === 'y') {
                e.preventDefault();
                // Activate planar drag: hide built-in gizmos, enable our custom mode
                gizmoService.gizmoMode = undefined;
                setPlanarMode(true);
            } else if (key === 'q') {
                e.preventDefault();
                gizmoService.gizmoMode = undefined;
                setPlanarMode(false);
            }
        };

        // Start with the planar drag gizmo active — unless the scene is
        // already running, in which case play mode owns the pointer and the
        // stash hands planar back on stop.
        gizmoService.gizmoMode = undefined;
        stashedPlanar = true;
        setPlanarMode(!inPlayMode);

        window.addEventListener('keydown', onKeyDown);

        return {
            dispose: () => {
                if (_gizmoService === gizmoService) _gizmoService = null;
                window.removeEventListener('keydown', onKeyDown);
                window.removeEventListener('trace-viewer:playmode-changed', onPlayModeChanged);
                modeObserver?.remove();
                setPlanarMode(false);
            }
        };
    }
};
